/**
 * Video API Client
 * Utilities for admin video playback URL generation
 */

import { getAuthToken } from "@/lib/services/auth-service";

const CORE_API_URL =
  import.meta.env.VITE_CORE_API_URL ||
  "https://vizi-core-api.vizimix.workers.dev";

export interface PlaybackUrlResponse {
  url: string;
  expiresAt: number;
  expiresIn: number;
}

export interface PlaybackUrlError {
  error: string;
}

/**
 * Get a presigned playback URL for a video
 * Note: This currently requires the requesting user to own the video's project.
 * For admin access to other users' videos, an admin-specific endpoint may be needed.
 */
export async function getPlaybackUrl(
  r2Key: string,
  bucket: "videos" | "renders" = "renders",
): Promise<PlaybackUrlResponse> {
  const token = await getAuthToken();
  if (!token) {
    throw new Error("Authentication required");
  }

  const params = new URLSearchParams({
    key: r2Key,
    bucket,
  });

  const response = await fetch(`${CORE_API_URL}/video/playback-url?${params}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = (await response
      .json()
      .catch(() => ({ error: response.statusText }))) as PlaybackUrlError;
    throw new Error(error.error || "Failed to get playback URL");
  }

  return response.json();
}

/**
 * Get presigned playback URLs for multiple videos
 * Returns results array with urls or errors for each key
 */
export async function batchGetPlaybackUrls(
  keys: Array<{ key: string; bucket: "videos" | "renders" }>,
): Promise<
  Array<{
    key: string;
    url?: string;
    expiresAt?: number;
    error?: string;
  }>
> {
  const token = await getAuthToken();
  if (!token) {
    throw new Error("Authentication required");
  }

  const response = await fetch(`${CORE_API_URL}/video/batch-playback-urls`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ keys }),
  });

  if (!response.ok) {
    const error = (await response
      .json()
      .catch(() => ({ error: response.statusText }))) as PlaybackUrlError;
    throw new Error(error.error || "Failed to get playback URLs");
  }

  const data = (await response.json()) as {
    results: Array<{
      key: string;
      url?: string;
      expiresAt?: number;
      error?: string;
    }>;
  };
  return data.results;
}

/**
 * Check if a playback URL has expired or is about to expire
 * Returns true if URL has less than 60 seconds remaining
 */
export function isUrlExpired(expiresAt: number): boolean {
  return Date.now() > (expiresAt - 60) * 1000;
}
